import {
    action,
    IReactionDisposer,
    observable,
    reaction,
    runInAction,
    makeObservable,
} from "mobx";
import {ServiceBase} from "../ServiceBase";
import {IServiceFactoryExtended} from "../service-factory-extended.interface";
import {IPlayerBurnoutService} from "./player-burnout.service.interface";
import {CGPEventSubscription} from "../sdk/cgp-sdk.interface";
import {IOmgHandlerHook} from "../external-links/handlers/omg/omg-handler.hook.interface";
import {GameModeEnum} from "../game-runner/interfaces/game-context/game-context-view-model.interface";
import {ExternalLinksZIndexes} from "../external-links/external-links-z-indexes";
import {IFirebaseTopicRegistration} from "../firebase/topics/firebase-topic-view-model.interface";

export class PlayerBurnoutService extends ServiceBase implements IPlayerBurnoutService {
    constructor(serviceFactory: IServiceFactoryExtended) {
        super(serviceFactory);

        makeObservable(this);

        this.reaction(() => this.services.user.isAuthorized,
            this._onAuthStateChange,
            { fireImmediately: true }
        );
    }

    @observable.ref
    public renderAnimation = false;

    @observable
    private _currentlyDisplayedOmg: IOmgHandlerHook[] = [];

    private _burnoutMessageEventSubscription: IFirebaseTopicRegistration | null = null;
    private _omgDisplayedEventSubscription: CGPEventSubscription | null = null;
    private _currentRunningGameReaction: IReactionDisposer | null = null;


    private _onAuthStateChange = (isAuthorized: boolean) => {
        if (isAuthorized) {
            this._subscribe();
        } else {
            this._unsubscribe();
            // Actually I want to remove the floating button if any exists
            this.setRenderAnimation(false);
        }
    }

    private get _isRealMoneyPrize(): boolean {
        return this.services.configuration.servicesConfigs.PlayerBurnout.isRealMoneyPrize;
    }

    private _subscribe() {
        this._burnoutMessageEventSubscription = this.services.firebase.topics.messages.events.burnout.registerListener(this._onBurnoutMessageReceivedEvent);
        this.services.gameRunner.then(gameRunner => {
            this._currentRunningGameReaction = reaction(() => gameRunner.currentRunningGame,
                (currentRunningGame) => {
                    this._clearCurrentDisplayOmg();
                    if(currentRunningGame) {
                        if(currentRunningGame.gameMode === GameModeEnum.Real && !this._isRealMoneyPrize) {
                            this._omgDisplayedEventSubscription = this.services.sdk.events.onOmgDisplayed(this._onOmgDisplayedEvent);
                        }
                    } else {
                        this.setRenderAnimation(false);
                        this._omgDisplayedEventSubscription?.off();
                        this._omgDisplayedEventSubscription = null;
                    }
                });
        })

    }

    private _unsubscribe() {
        this._clearCurrentDisplayOmg();

        this._burnoutMessageEventSubscription?.unregister();
        this._omgDisplayedEventSubscription?.off();

        this._burnoutMessageEventSubscription = null;
        this._omgDisplayedEventSubscription = null;

        if(this._currentRunningGameReaction) {
            this._currentRunningGameReaction();
            this._currentRunningGameReaction = null;
        }
    }

    @action.bound
    private _onOmgDisplayedEvent = (omgHook: IOmgHandlerHook) => {
        this._currentlyDisplayedOmg[0] = omgHook;

    };

    private _clearCurrentDisplayOmg(): void {
        runInAction(() => {
            this._currentlyDisplayedOmg = [];
        });
    }

    private _onBurnoutMessageReceivedEvent = () => {
        // At any moment we can have only one animation/gift button on the screen.
        // So if a burnout message is received and I need to show the animation but there is another gift button already on the screen
        // then I need to remove the previous one and then reshow the animation
        // Product management is assuring me this will not happen but I need to be sure
        if (this.renderAnimation === true) {
            this.setRenderAnimation(false);
            setTimeout(() => {
                this.setRenderAnimation(true);
            }, 200);
        } else {
            this.setRenderAnimation(true);
        }
    };

    public onSafeUnmountNotice() {
        this.setRenderAnimation(false);
    }

    @action.bound
    private setRenderAnimation(val: boolean) {
        this.renderAnimation = val;
    }

    public showOmg = () => {
        if(this._currentlyDisplayedOmg.length > 0) {
            this._currentlyDisplayedOmg[0].bringToFront();
            this._clearCurrentDisplayOmg();
        }
    }

    public checkIfOmgDisplayed = () => {
        return this._currentlyDisplayedOmg.length > 0;
    }

    public getBurnoutAnimationZIndex() {
        return ExternalLinksZIndexes.BurnOutAnimation;
    }
}
